import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { Receipt, Plus, Trash2, Send, Save, Loader2, FileDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReceiptEditor = ({ entityId, open, onOpenChange, token, language }) => {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    const ok = await save();
    if (!ok) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/receipts/${receipt.receipt_id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recibo_wegoo_${entityId?.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'pt' ? 'PDF descarregado' : 'PDF downloaded');
    } catch (e) {
      toast.error(language === 'pt' ? 'Erro ao gerar PDF' : 'PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  const fetchReceipt = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/receipts/by-entity/${entityId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setReceipt(await res.json());
      else toast.error(language === 'pt' ? 'Erro ao carregar recibo' : 'Error loading receipt');
    } catch (e) {
      toast.error(language === 'pt' ? 'Erro de rede' : 'Network error');
    } finally {
      setLoading(false);
    }
  }, [entityId, token, language]);

  useEffect(() => {
    if (open && entityId) fetchReceipt();
  }, [open, entityId, fetchReceipt]);

  const liveTotal = receipt
    ? (Number(receipt.base_total || 0) + (receipt.adjustments || []).reduce((s, a) => s + (Number(a.amount) || 0), 0))
    : 0;

  const setAdj = (idx, field, value) => {
    setReceipt(prev => ({
      ...prev,
      adjustments: prev.adjustments.map((a, i) => i === idx ? { ...a, [field]: value } : a)
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/receipts/${receipt.receipt_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adjustments: (receipt.adjustments || []).map(a => ({ label: a.label, amount: Number(a.amount) || 0 })),
          notes: receipt.notes || ''
        })
      });
      if (res.ok) {
        setReceipt(await res.json());
        toast.success(language === 'pt' ? 'Recibo guardado' : 'Receipt saved');
        return true;
      }
      toast.error(language === 'pt' ? 'Erro ao guardar' : 'Save failed');
      return false;
    } catch (e) {
      toast.error(language === 'pt' ? 'Erro de rede' : 'Network error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    const ok = await save();
    if (!ok) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/receipts/${receipt.receipt_id}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(language === 'pt' ? `Recibo enviado para ${data.sent_to}` : `Receipt sent to ${data.sent_to}`);
        fetchReceipt();
      } else {
        toast.error(data.detail || (language === 'pt' ? 'Falha no envio' : 'Send failed'));
      }
    } catch (e) {
      toast.error(language === 'pt' ? 'Erro de rede' : 'Network error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="receipt-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="text-[#FF6B35]" size={20} />
            {language === 'pt' ? 'Recibo' : 'Receipt'} #{entityId?.slice(-8)}
          </DialogTitle>
        </DialogHeader>

        {loading || !receipt ? (
          <div className="py-8 text-center"><Loader2 className="animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="bg-[#F4F1EA] rounded-xl p-4 text-sm space-y-1">
              {(receipt.items || []).map((i, idx) => (
                <div key={idx} className="flex justify-between text-[#004E64]">
                  <span>{i.quantity}× {i.name}</span>
                  <span>€{Number(i.total || 0).toFixed(2)}</span>
                </div>
              ))}
              <Separator className="my-2" />
              <div className="flex justify-between text-[#9FA2B4]"><span>Subtotal</span><span>€{Number(receipt.subtotal || 0).toFixed(2)}</span></div>
              {Number(receipt.delivery_fee || 0) > 0 && <div className="flex justify-between text-[#9FA2B4]"><span>{language === 'pt' ? 'Taxa de entrega' : 'Delivery fee'}</span><span>€{Number(receipt.delivery_fee).toFixed(2)}</span></div>}
              {Number(receipt.deposit || 0) > 0 && <div className="flex justify-between text-[#9FA2B4]"><span>{language === 'pt' ? 'Depósito' : 'Deposit'}</span><span>€{Number(receipt.deposit).toFixed(2)}</span></div>}
              {Number(receipt.discount || 0) > 0 && <div className="flex justify-between text-[#2EC4B6]"><span>{language === 'pt' ? 'Desconto' : 'Discount'}</span><span>−€{Number(receipt.discount).toFixed(2)}</span></div>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#004E64]">{language === 'pt' ? 'Ajustes (ex: dedução do depósito)' : 'Adjustments (e.g. deposit deduction)'}</p>
                <Button
                  variant="outline" size="sm" className="rounded-full text-xs"
                  onClick={() => setReceipt(prev => ({ ...prev, adjustments: [...(prev.adjustments || []), { label: '', amount: '' }] }))}
                  data-testid="receipt-add-adjustment-btn"
                >
                  <Plus size={12} className="mr-1" />{language === 'pt' ? 'Adicionar' : 'Add'}
                </Button>
              </div>
              {(receipt.adjustments || []).length === 0 && (
                <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Sem ajustes. Usa valores negativos para deduções (ex: −15).' : 'No adjustments. Use negative values for deductions (e.g. −15).'}</p>
              )}
              <div className="space-y-2">
                {(receipt.adjustments || []).map((a, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={a.label}
                      onChange={e => setAdj(idx, 'label', e.target.value)}
                      placeholder={language === 'pt' ? 'Descrição (ex: Dedução do depósito)' : 'Description'}
                      className="flex-1 h-9 text-sm"
                      data-testid={`receipt-adj-label-${idx}`}
                    />
                    <Input
                      type="number" step="0.01"
                      value={a.amount}
                      onChange={e => setAdj(idx, 'amount', e.target.value)}
                      placeholder="-15.00"
                      className="w-28 h-9 text-sm"
                      data-testid={`receipt-adj-amount-${idx}`}
                    />
                    <button
                      onClick={() => setReceipt(prev => ({ ...prev, adjustments: prev.adjustments.filter((_, i) => i !== idx) }))}
                      className="text-red-400 hover:text-red-600"
                      data-testid={`receipt-adj-delete-${idx}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#004E64] mb-1">{language === 'pt' ? 'Notas no recibo' : 'Receipt notes'}</p>
              <Textarea
                value={receipt.notes || ''}
                onChange={e => setReceipt(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={language === 'pt' ? 'Ex: Foram retidos €15 do depósito por danos no equipamento.' : 'e.g. €15 withheld from deposit.'}
                className="text-sm"
                rows={2}
                data-testid="receipt-notes-input"
              />
            </div>

            <div className="flex justify-between items-center bg-[#FF6B35]/5 rounded-xl p-3">
              <span className="font-bold text-[#004E64]">{language === 'pt' ? 'Total final' : 'Final total'}</span>
              <span className="font-bold text-[#FF6B35] text-lg" data-testid="receipt-final-total">€{liveTotal.toFixed(2)}</span>
            </div>

            {receipt.sent_at && (
              <p className="text-xs text-[#2EC4B6]">
                ✓ {language === 'pt' ? 'Enviado' : 'Sent'} {receipt.sent_count}× · {language === 'pt' ? 'último para' : 'last to'} {receipt.sent_to} ({new Date(receipt.sent_at).toLocaleString('pt-PT')})
              </p>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={save} disabled={saving || sending || downloading} className="flex-1 rounded-full" data-testid="receipt-save-btn">
                <Save size={14} className="mr-1" />{language === 'pt' ? 'Guardar' : 'Save'}
              </Button>
              <Button variant="outline" onClick={downloadPdf} disabled={saving || sending || downloading} className="flex-1 rounded-full border-[#004E64]/30 text-[#004E64]" data-testid="receipt-pdf-btn">
                {downloading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />}
                {language === 'pt' ? 'Guardar PDF' : 'Save PDF'}
              </Button>
              <Button onClick={send} disabled={saving || sending || downloading} className="flex-1 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" data-testid="receipt-send-btn">
                {sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                {receipt.sent_count > 0 ? (language === 'pt' ? 'Reenviar' : 'Resend') : (language === 'pt' ? 'Enviar Email' : 'Send Email')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptEditor;
